import React, { useState, useRef, useCallback } from 'react';
import { RaceLap } from '../../models/TelemetryData';
import { formatMilliseconds } from '../../utils/time';
import LapDrawer from './LapDrawer';

// Colors for middle laps (neither fastest nor slowest).
// Intentionally excludes gold/amber hues — the reference lap is gold (#d4a843),
// so no lap color should be close enough to be confused with it.
const LAP_COLORS_MIDDLE = [
  '#f0f0f0', // white
  '#e056fd', // magenta
  '#9b59b6', // purple
  '#3498db', // blue
  '#48dbfb', // cyan
  '#1abc9c', // teal
];

export const REF_LAP_ID = '__ref__';

/**
 * Get a stable color for a lap by its ID.
 * allLapIds must be sorted fastest-first.
 * Fastest lap is always green, slowest is always red,
 * middle laps cycle through the remaining palette.
 */
export function getLapColor(lapId: string, allLapIds: string[]): string {
  if (lapId === REF_LAP_ID) return '#d4a843';
  const index = allLapIds.indexOf(lapId);
  if (index < 0) return LAP_COLORS_MIDDLE[0];
  if (allLapIds.length === 1) return '#2ecc71';
  if (index === 0) return '#2ecc71';
  if (index === allLapIds.length - 1) return '#e74c3c';
  return LAP_COLORS_MIDDLE[(index - 1) % LAP_COLORS_MIDDLE.length];
}

interface LapSelectorProps {
  laps: RaceLap[];
  selectedLapId: string;
  checkedLapIds: string[];
  loadingLapIds: string[];
  failedLapIds: string[];
  onToggleLap: (lapId: string) => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
  allLapIds: string[];
  refChip?: { label: string; time: number } | null;
  alignMode: 'time' | 'dist';
  anchorLapId: string | null;
  onAnchorChange: (lapId: string) => void;
}

const LapSelector: React.FC<LapSelectorProps> = ({
  laps,
  selectedLapId,
  checkedLapIds,
  loadingLapIds,
  failedLapIds,
  onToggleLap,
  isExpanded,
  onToggleExpand,
  allLapIds,
  refChip,
  alignMode,
  anchorLapId,
  onAnchorChange,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null);
  const addBtnRef = useRef<HTMLSpanElement>(null);

  const openDrawer = useCallback(() => {
    if (addBtnRef.current) {
      const rect = addBtnRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left });
    }
    setDrawerOpen(true);
  }, []);

  return (
    <div className="tele-toolbar">
      <span className="tele-toolbar-label">Laps</span>

      <div className="tele-toolbar-chips">
        {refChip && (
          <>
            <span className="lap-chip lap-chip-ref">
              {alignMode === 'time' && (
                <span
                  className={`lap-anchor-star${anchorLapId === REF_LAP_ID ? ' active' : ''}`}
                  title="Anchor lap for track-map timing"
                  onClick={(e) => { e.stopPropagation(); onAnchorChange(REF_LAP_ID); }}
                >★</span>
              )}
              <span className="ref-badge">REF</span>
              {refChip.label}
              <span className="lap-chip-time">{formatMilliseconds(refChip.time)}</span>
            </span>
            <span className="lap-chip-divider" />
          </>
        )}
        {checkedLapIds.map((lapId) => {
          const lap = laps.find((l) => l.id === lapId);
          if (!lap) return null;

          const lapColor = getLapColor(lapId, allLapIds);
          const isLoading = loadingLapIds.includes(lapId);

          return (
            <span
              key={lapId}
              className="lap-chip active"
              style={{ color: lapColor, borderBottom: '2px solid ' + lapColor }}
            >
              {alignMode === 'time' && (
                <span
                  className={`lap-anchor-star${anchorLapId === lapId ? ' active' : ''}`}
                  title="Anchor lap for track-map timing"
                  onClick={(e) => { e.stopPropagation(); onAnchorChange(lapId); }}
                >★</span>
              )}
              L{lap.lap}
              <span className="lap-chip-time">{formatMilliseconds(lap.time)}</span>
              {isLoading ? (
                <span className="lap-spinner" />
              ) : (
                <span
                  className="lap-chip-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleLap(lapId);
                  }}
                >
                  &times;
                </span>
              )}
            </span>
          );
        })}

        <span className="lap-chip-add-wrapper">
          <span
            ref={addBtnRef}
            className="lap-chip lap-chip-add"
            onClick={() => drawerOpen ? setDrawerOpen(false) : openDrawer()}
          >
            + Add lap
          </span>
        </span>
      </div>

      <button className="tele-expand-btn" onClick={onToggleExpand} title={isExpanded ? 'Collapse' : 'Expand'}>
        {isExpanded
          ? <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.8"><line x1="1" y1="1" x2="9" y2="9"/><line x1="9" y1="1" x2="1" y2="9"/></svg>
          : <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.8"><polyline points="0,3 0,0 3,0"/><polyline points="7,0 10,0 10,3"/><polyline points="10,7 10,10 7,10"/><polyline points="3,10 0,10 0,7"/></svg>
        }
      </button>

      <LapDrawer
            isOpen={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            dropdownPos={dropdownPos}
            laps={laps}
            selectedLapId={selectedLapId}
            checkedLapIds={checkedLapIds}
            loadingLapIds={loadingLapIds}
            failedLapIds={failedLapIds}
            onToggleLap={onToggleLap}
            allLapIds={allLapIds}
          />
    </div>
  );
};

export default LapSelector;
